import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';

import { HsgModal } from '@hhsg/hsg-asc';

import * as moment from 'moment';

import { Constants } from 'src/shared/utilities/constants.util';
import { CostCenter } from 'src/shared/models/cost-center.model';
import { MiscUtil } from 'src/shared/utilities/misc.util';
import { MissingBlock } from 'src/shared/models/missing-block.model';
import { MissingBlocksModalComponent } from '../missing-blocks-modal/missing-blocks-modal.component';
import { MissingUosBlock } from 'src/shared/models/missing-uos-block.model';
import { ShiftBlock } from 'src/shared/models/shift-block.model';
import { ShiftBlockService } from 'src/shared/services/shift-block.service';
import { UnitOfServiceRecord } from 'src/shared/models/unit-of-service-record.model';
import { UnitOfServiceRecordService } from 'src/shared/services/uos-record.service';
import { UnitsOfService } from 'src/shared/models/units-of-service.model';
import { UnitsOfServiceService } from 'src/shared/services/units-of-service.service';
import { WorkflowConstants } from '../workflow-constants.util';
import { WorkflowPageUtil } from '../base-classes/workflow-page/workflow-page.util';

@Component({
    selector: 'smt-workflow-header',
    templateUrl: './workflow-header.component.html',
    styleUrls: ['./workflow-header.component.scss']
})
export class WorkflowHeaderComponent implements OnInit {
    @Input() title: string;
    @Input() public costCenter: CostCenter;
    @Input() public shiftBlock: ShiftBlock;
    @Input() public workflowTime: string;

    @Output() saveCompleted = new EventEmitter<any>();

    public readonly numberOfDays = 7;
    public missingBlocks = [] as MissingBlock[];
    public missingUosBlocks: MissingUosBlock[] = [];    
    public now = moment().local();
    public endDate = this.now.format(WorkflowConstants.MomentFormat);
    public startDate = this.now.subtract(this.numberOfDays, 'days').format(WorkflowConstants.MomentFormat);
    public enableMissingUosModal = false;
    public selectedMissingUosBlock: MissingUosBlock;
    public minValue = WorkflowConstants.UosMinValue;
    public maxValue = WorkflowConstants.UosMaxValue;
    public unitOfService: UnitsOfService;
    public showSaveError = false;
    public isMissingBlocksLoading = false;
    public isMissingUosBlocksLoading = false;

    private totalUosByDay = false;    

    public enterUOSForm:UntypedFormGroup = new UntypedFormGroup({});

    constructor(
        private modal: HsgModal,
        private shiftBlockService: ShiftBlockService,
        private unitOfServiceService: UnitsOfServiceService,
        private unitOfServiceRecordService: UnitOfServiceRecordService) { 
    }

    ngOnInit() {
        if (!this.costCenter || !this.costCenter.id) {
            return;
        }
        
        this.totalUosByDay = (WorkflowPageUtil.hasAcuityConfiguration(this.costCenter) ||
                            WorkflowPageUtil.hasAcuityWithRoomsConfiguration(this.costCenter) ||
                            WorkflowPageUtil.hasUosConfiguration(this.costCenter))
                            && !this.costCenter.ancillaryConfiguration.enterUOSByBlock;            

        this.isMissingBlocksLoading = true;

        this.shiftBlockService.loadMissingBlocks(this.costCenter.id, this.numberOfDays).then((missingBlocks: MissingBlock[]) => {
            this.missingBlocks = missingBlocks;

            this.isMissingBlocksLoading = false;
        });

        if (this.totalUosByDay) {
            this.unitOfServiceService.GetUnitsOfServiceById(this.costCenter.ancillaryConfiguration.unitOfServiceId).then((unitOfService) => {
                this.unitOfService = unitOfService;
            });

            this.loadMissingUosBlocks();
        }                
    }

    get changeCostCenter() {
        return Constants.CostCenterWorkflowUrl;
    }

    get changeShiftBlock() {
        return '/workflow/shift-block/' + this.costCenter.id;
    }

    closeMissingUosBlockModal() {
        // hide the modal pop up.
        this.enableMissingUosModal = false;     

        // de-select the missing block.
        this.selectedMissingUosBlock = null;
    }

    private controlIsValid(control: any): boolean {
        return control && !control.valid && (control.touched || control.dirty);
    }

    enableMissingModalSave(): boolean {
        if (!this.totalUosByDay || !this.selectedMissingUosBlock) {
            return false;
        }

        return !this.selectedMissingUosBlock.missingBlocks.some(m => 
                                                            !MiscUtil.isValidNonNegativeNumber(m.total) || 
                                                            this.valueOutOfRange(m.shiftBlockId) || 
                                                            this.valueOutOfPattern(m.shiftBlockId));
    }    

    formatMissingBlockPreviousDateOnly(missingBlock: MissingBlock): string {
        return moment(missingBlock.previousWorkingDay).format(WorkflowConstants.MomentFormat);
    }

    formatMissingBlockHoursOnly(missingBlock: MissingBlock): string {
        return this.shiftBlockService.formatShiftTimeHoursOnly(missingBlock.shiftBlock, missingBlock.workflowTimeFacility);
    }

    formatMissingUosBlock(missingUosBlock: MissingUosBlock): string {
        let missingBlocks = missingUosBlock.missingBlocks;
        let firstMissingBlock = missingBlocks[0];
        let shiftBlock = { ...firstMissingBlock.shiftBlock };
        
        shiftBlock.endHour = missingBlocks[missingBlocks.length - 1].shiftBlock.endHour;

        return this.shiftBlockService.formatShiftTime(shiftBlock, firstMissingBlock.workflowTimeFacility);
    }

    formattedShiftBlockRange() {
        if (!this.shiftBlock || !this.workflowTime) return '';

        return this.shiftBlockService.formatShiftTime(this.shiftBlock, this.workflowTime);
    }

    private getControlByShiftBlock(shiftBlockId: number) {
        const safeShitBlockName = encodeURIComponent(this.getShiftBlockControlName(shiftBlockId));
        return this.enterUOSForm.get(safeShitBlockName.replace(/%20/g, ' '));
    }    

    getShiftBlockControlName(shiftBlockId: number) : string {
        return 'block' + shiftBlockId;
    }

    getShiftBlockLabel(shiftBlock: ShiftBlock, workflowDateFacility: Date) {
        return this.shiftBlockService.formatShiftTime(shiftBlock, workflowDateFacility);
    }

    private loadMissingUosBlocks() {
        this.isMissingUosBlocksLoading = true;
        this.missingUosBlocks = [];
        let validators = [ Validators.required, Validators.pattern(Constants.NoDecimalRegex), Validators.min(this.minValue), Validators.max(this.maxValue) ];
                        
        this.shiftBlockService.loadMissingUosBlocks(this.costCenter.id, this.numberOfDays).then((missingUosBlocks: MissingUosBlock[]) => {
            this.missingUosBlocks = missingUosBlocks;              
        
            this.missingUosBlocks.forEach(u => {
                u.missingBlocks.forEach(m => {                    
                    this.enterUOSForm.addControl(this.getShiftBlockControlName(m.shiftBlock.id), new UntypedFormControl('', validators));
                });
            });

            this.isMissingUosBlocksLoading = false;
        });
    }    

    get missingBlocksCount() : number {
        return (!this.missingBlocksLoading && this.missingBlocks) ? this.missingBlocks.length : 0;
    }

    get missingBlocksLoading(): boolean {
        return this.isMissingBlocksLoading || this.isMissingUosBlocksLoading;
    }

    get missingUosBlocksCount(): number {
        return (!this.missingBlocksLoading && this.totalUosByDay && this.missingUosBlocks) ? this.missingUosBlocks.length : 0;
    }

    get missingUosModalTitle(): string {
        if (!this.unitOfService) {
            return '';
        }

        return 'Enter Total ' + this.unitOfService.category;
    }

    openMissingBlocks() {
        this.modal.open(MissingBlocksModalComponent, {
            data: {
                title: `Missing Blocks - ${this.startDate} - ${this.endDate}`,
                costCenter: this.costCenter,
                size: { width: '400px', height: '500px' },
                missingBlocks: this.missingBlocks,
                acceptText: 'Close'
            }
        });
    }

    openMissingUosBlocks() {
        this.showSaveError = false;
        this.enableMissingUosModal = true;        
    }  
    
    async saveMissingUosModal() {
        let uosRecords: UnitOfServiceRecord[] = [];

        this.missingUosBlocks.forEach(m => {
            m.missingBlocks.filter(b => MiscUtil.isValidPositiveNumber(b.total)).forEach(b => {
                uosRecords.push({
                    id: 0,
                    isProjected: false,
                    shiftBlockId: b.shiftBlockId,
                    total: b.total,
                    unitOfServiceId: this.unitOfService.id,
                    workflowTime: b.workflowTime,
                    workflowTimeFacility: b.workflowTimeFacility,
                    importedAmount: null
                });
            });
        });

        let result = await this.unitOfServiceRecordService.saveUnitOfServiceRecords(uosRecords, this.costCenter.id);    

        if (result) {            
            this.showSaveError = false;
            this.loadMissingUosBlocks();  
            this.closeMissingUosBlockModal();
            this.saveCompleted.emit();
        }
        else {
            this.showSaveError = true;
        }
    }

    get unitOfServiceCategory(): string {
        if (!this.unitOfService) {
            return '';
        }

        return this.unitOfService.category;
    }
    
    valueOutOfPattern(shiftBlockId: number): boolean {
        let control = this.getControlByShiftBlock(shiftBlockId);
        return this.controlIsValid(control) && control.errors.pattern;
    }

    valueOutOfRange(shiftBlockId: number) : boolean {
        let control = this.getControlByShiftBlock(shiftBlockId);
        return this.controlIsValid(control) && (control.errors.min || control.errors.max);
    }    
}
